import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../utils/supabaseClient';
import { setUserId, trackAuthEvent, trackConversionEvent } from '../utils/analytics';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check active session
        const getSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            setSession(session);
            setUser(session?.user ?? null);
            
            // Set user ID in analytics if user is logged in
            if (session?.user) {
                setUserId(session.user.id);
            }
            
            setLoading(false);
        };

        getSession();

        // Listen for changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
            setUser(session?.user ?? null);
            
            // Track authentication events
            if (_event === 'SIGNED_IN' && session?.user) {
                setUserId(session.user.id);
                trackAuthEvent('login', 'email', 'success');
                trackConversionEvent('sign_up');
            } else if (_event === 'SIGNED_OUT') {
                trackAuthEvent('logout', 'email', 'success');
                // Clear user ID from analytics
                setUserId(null);
            } else if (_event === 'TOKEN_REFRESHED') {
                // Track token refresh for session management
                trackAuthEvent('token_refresh', 'auto', 'success');
            }
            
            setLoading(false);

            // Redirect after login if a protected route was saved
            if (_event === 'SIGNED_IN' && session) {
                const redirect = sessionStorage.getItem('redirectAfterLogin');
                if (redirect) {
                    sessionStorage.removeItem('redirectAfterLogin');
                    window.location.replace(redirect);
                }
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const value = {
        signUp: async (data) => {
            try {
                const result = await supabase.auth.signUp(data);
                if (result.error) {
                    trackAuthEvent('sign_up', 'email', 'error');
                } else {
                    trackAuthEvent('sign_up', 'email', 'initiated');
                }
                return result;
            } catch (error) {
                trackAuthEvent('sign_up', 'email', 'error');
                throw error;
            }
        },
        signIn: async (data) => {
            try {
                const result = await supabase.auth.signInWithPassword(data);
                if (result.error) {
                    trackAuthEvent('login', 'email', 'error');
                }
                return result;
            } catch (error) {
                trackAuthEvent('login', 'email', 'error');
                throw error;
            }
        },
        signOut: async () => {
            try {
                const result = await supabase.auth.signOut();
                // Logout tracking is handled in the auth state change listener above
                return result;
            } catch (error) {
                trackAuthEvent('logout', 'email', 'error');
                throw error;
            }
        },
        user,
        session,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};
